import type { CustomerDetailRecord } from '@forq/customers-data';
import { effectiveTaxCode } from '../taxCodes';
import type { Invoice } from '../types';
import type { EInvoiceBuyer } from './xrechnung';

/**
 * Turns a FORQ customer into the invoice recipient an XRechnung needs. The invoice itself only
 * ever carried number and name — everything else (address, USt-IdNr.) lives on the customer, and
 * is mandatory on an e-invoice.
 */
export function buyerFromCustomer(customer: CustomerDetailRecord, invoice: Invoice): EInvoiceBuyer {
  // The billing address wins; otherwise the primary one; otherwise whatever exists.
  const address = customer.addresses.find((entry) => entry.kind === 'billing')
    ?? customer.addresses.find((entry) => entry.primary)
    ?? customer.addresses[0];
  return {
    name: customer.displayName,
    street: address?.street ?? '',
    postalCode: address?.postalCode ?? '',
    city: address?.city ?? '',
    countryCode: address?.countryCode || 'DE',
    vatId: customer.vatId,
    email: customer.email,
    // BT-10 ist Pflicht. Bei einem ÖFFENTLICHEN Auftraggeber MUSS dort die Leitweg-ID stehen --
    // sie adressiert die Rechnung im Behördennetz, eine Vorgangsnummer landet nirgends. Für alle
    // anderen bleibt die Vorgangsnummer die Referenz, die der Kunde wiedererkennt.
    buyerReference: customer.agreements.leitwegId.trim()
      || invoice.sourceNumber
      || invoice.invoiceNumber
      || invoice.proposalNumber,
  };
}

/**
 * Leitweg-ID: Grobadressierung (2--12 Ziffern), optional Feinadressierung (bis 30 Zeichen), dann
 * die zweistellige Prüfziffer -- z. B. `991-01234-56` oder `04011000-1234512345-06`.
 *
 * Bewusst NUR die Form geprüft, nicht die Prüfziffer: deren Berechnung ist in der Koordinierungs-
 * stelle-Spezifikation geregelt und ändert sich; eine falsch nachgebaute Prüfung würde gültige
 * IDs abweisen. Ein Zahlendreher fällt beim Empfänger auf, eine vertippte Form schon hier.
 */
export function isValidLeitwegId(value: string): boolean {
  return /^\d{2,12}(-[0-9A-Za-z]{1,30})?-\d{2}$/.test(value.trim());
}

/**
 * What is missing on the RECIPIENT side before an e-invoice may be produced. Kept apart from the
 * seller check so the user learns which record to go and fix.
 */
export function buyerIssues(
  buyer: EInvoiceBuyer,
  invoice: Invoice,
  /** Die am Kunden hinterlegte Leitweg-ID -- gesetzt = öffentlicher Auftraggeber. */
  leitwegId = '',
): readonly string[] {
  const issues: string[] = [];
  if (!buyer.name.trim()) issues.push('Der Kunde hat keinen Namen.');
  if (!buyer.street.trim() || !buyer.postalCode.trim() || !buyer.city.trim()) {
    issues.push(`Für ${buyer.name || 'den Kunden'} fehlt eine vollständige Rechnungsanschrift (Straße, PLZ, Ort).`);
  }
  if (!/^[A-Z]{2}$/.test(buyer.countryCode)) issues.push('Der Ländercode der Rechnungsanschrift fehlt.');
  if (!buyer.buyerReference.trim()) issues.push('Leitweg-ID / Bestellreferenz fehlt (in der XRechnung Pflicht).');
  // Eine hinterlegte Leitweg-ID landet unverändert als BT-10 im Beleg -- ist sie vertippt, weist die
  // Behörde die Rechnung ab. Deshalb hier prüfen, nicht erst dort.
  if (leitwegId.trim() && !isValidLeitwegId(leitwegId)) {
    issues.push(`Die Leitweg-ID „${leitwegId.trim()}" hat kein gültiges Format (z. B. 991-01234-56).`);
  }

  // Reverse charge and intra-community supply are only valid with the recipient's VAT id.
  const needsVatId = invoice.lines.some((line) => {
    const code = effectiveTaxCode(line);
    return code === 'RC13B' || code === 'IG';
  });
  if (needsVatId && !buyer.vatId.trim()) {
    issues.push(`${buyer.name} braucht eine USt-IdNr.: die Rechnung enthält steuerfreie Positionen (§ 13b oder innergemeinschaftlich).`);
  }
  return issues;
}
